import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useState } from "react";

const services = [
  {
    id: 1,
    title: "Luxury Interiors",
    description:
      "Bespoke interior execution for high-end retail and aviation lounges. We bring world-class aesthetics to life with precision joinery and premium finishes.",
    image: "images/luxury-interiors.jpg",
  },
  {
    id: 2,
    title: "Civil Works",
    description:
      "Heavy-duty structural execution, from airport terminals to foundation strengthening. Our civil engineering team ensures unshakeable stability.",
    image: "images/civil-works.jpg",
  },
  {
    id: 3,
    title: "MEP Services",
    description:
      "Integrated Mechanical, Electrical, and Plumbing solutions. We design complex systems that power 24/7 operational hubs seamlessly.",
    image: "images/mep-services.jpg",
  },
  {
    id: 4,
    title: "Design Consultancy",
    description:
      "Strategic design planning that balances aesthetics with operational efficiency. We turn blueprints into functional masterpieces.",
    image: "images/design-consultancy.jpg",
  },
];

export const Services = () => {
  const [activeId, setActiveId] = useState<number>(1);

  return (
    <section className="bg-stone-50 py-24 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-primary font-medium tracking-widest uppercase mb-4">
            Our Expertise
          </p>
          <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-6">
            Building Excellence
          </h2>
          <div className="w-24 h-1 bg-gray-200 mx-auto" />
        </div>

        <div className="flex flex-col lg:flex-row h-175 w-full gap-4">
          {services.map((service) => (
            <motion.div
              key={service.id}
              layout
              onHoverStart={() => setActiveId(service.id)}
              onClick={() => setActiveId(service.id)}
              className={cn(
                "relative h-full rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 ease-in-out group",
                activeId === service.id ? "flex-3" : "flex-1",
              )}>
              {/* Background Image */}
              <img
                src={service.image}
                alt={service.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />

              {/* Overlay - Darker on inactive, lighter gradient on active */}
              <div
                className={cn(
                  "absolute inset-0 transition-colors duration-500",
                  activeId === service.id
                    ? "bg-linear-to-t from-black/90 via-black/40 to-transparent"
                    : "bg-black/60 group-hover:bg-black/50",
                )}
              />

              {/* Content */}
              <div className="absolute inset-0 p-8 flex flex-col justify-end">
                {activeId === service.id ? (
                  /* Active State Content */
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.3 }}
                    className="flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-3xl md:text-5xl font-serif text-white leading-tight">
                        {service.title}
                      </h3>
                      <div className="p-3 bg-white/10 backdrop-blur-md rounded-full text-white hover:bg-primary transition-colors">
                        <ArrowUpRight className="w-6 h-6" />
                      </div>
                    </div>
                    <p className="text-gray-300 text-lg leading-relaxed max-w-xl">
                      {service.description}
                    </p>
                  </motion.div>
                ) : (
                  /* Inactive State Content - Vertical Title */
                  <div className="h-full flex items-center justify-center">
                    <h3 className="text-2xl font-serif text-white/80 whitespace-nowrap tracking-wider -rotate-90 origin-center lg:-rotate-90 lg:translate-y-0 translate-y-0">
                      {service.title}
                    </h3>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
